// ゲーム機画面内モーダルの共通ガワ（確定デザイン：記号なし）。
// 白カード＋硬い段差シャドウ。ヘッダーは「用途色の丸座アイコン＋黒タイトル」。
// 色の掟：面は白、用途色はアイコン座・入力フォーカス・主ボタンの“点”だけ。
// onClose 指定時のみ、ヘッダー右上に閉じる(×)ボタンを出す（クリア型Modal用）。
import { ReactNode, useState, useEffect } from "react";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import I18n from "common/i18n";
import { btnDelay, usePlaySound } from "common/utils";
import { useScreenModalContainer } from "./ScreenModal";

/** 枠からはみ出さないように詰める余白（左右合計） */
const PANEL_MARGIN = 24;
/** これより狭くすると中身が崩れるので下限にする */
const PANEL_MIN_WIDTH = 480;

/**
 * 置ける幅に合わせた実際のパネル幅。
 * 表示先コンテナ（倍率つきの全画面の層。論理 px で 1280 以上）より広い指定だと枠からはみ出すので、
 * コンテナの幅で頭打ちにする。
 */
const useFittedWidth = (width: number): number => {
  const container = useScreenModalContainer();
  const [available, setAvailable] = useState<number | null>(null);

  useEffect(() => {
    if (!container) return;
    const update = () => setAvailable(container.offsetWidth);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [container]);

  if (available == null) return width;
  // 指定より広げてはいけない（440px の確認ダイアログが広がってしまう）。
  // 詰めるのは置ける幅までで、下限を下回らせない
  return Math.min(width, Math.max(PANEL_MIN_WIDTH, available - PANEL_MARGIN));
};

export enum BuilderModalAccent {
  GREEN,
  BLUE,
  BROWN,
  DANGER,
  ORANGE,
}

const ACCENT_BADGE_BG: Record<BuilderModalAccent, string> = {
  [BuilderModalAccent.GREEN]: "bg-green-100",
  [BuilderModalAccent.BLUE]: "bg-blue-100",
  [BuilderModalAccent.BROWN]: "bg-brown-80",
  [BuilderModalAccent.DANGER]: "bg-danger",
  [BuilderModalAccent.ORANGE]: "bg-orange-100",
};

export const BuilderModalPanel = ({
  accent,
  icon,
  title,
  width,
  onClose,
  onHelp,
  headerAction,
  children,
}: {
  accent: BuilderModalAccent;
  icon: string;
  title: string;
  width: number;
  // 指定時のみヘッダー右上に × を表示
  onClose?: () => void;
  // 指定時のみタイトル右に「ヘルプ」ピルを表示
  onHelp?: () => void;
  // 指定時のみ見出しの右（× の手前）に置く操作。ページ送りで位置が動かない場所に置きたいもの用
  headerAction?: ReactNode;
  children: ReactNode;
}) => {
  const play = usePlaySound();
  const handleClose = () => {
    play();
    btnDelay(() => onClose?.());
  };
  const handleHelp = () => {
    play();
    btnDelay(() => onHelp?.());
  };

  const fittedWidth = useFittedWidth(width);

  return (
    <div
      style={{ width: fittedWidth }}
      className="flex-col-el relative border-solid border-white border-4 rounded-2xl bg-white overflow-hidden builder-piece-shadow"
    >
      {/* ヘッダー：用途色の丸座アイコン＋黒タイトル（ヘッダー帯は塗らない） */}
      <div className="flex-row-el items-center w-full gap-2 px-4 pt-4 pb-2">
        <div
          className={`flex-col-center shrink-0 w-10 h-10 rounded-xl ${ACCENT_BADGE_BG[accent]} shadow-md`}
        >
          <span className="material-symbols-outlined text-white type-md">{icon}</span>
        </div>
        <p className="type-text-left type-md text-textcolor-black flex-1 min-w-0">{title}</p>

        {onHelp && (
          <button
            type="button"
            onClick={handleHelp}
            className="type-inherit-font flex-row-el flex-center gap-1 shrink-0 h-9 px-3 rounded-full bg-blue-10 border-2 border-blue-60 cursor-pointer whitespace-nowrap"
          >
            <span className="flex-col-el flex-center type-sm text-blue-100">
              <HelpOutlineIcon fontSize="inherit" />
            </span>
            <span className="type-sm text-textcolor-black">{I18n.t("MSG_BUILDER_HELP_BTN")}</span>
          </button>
        )}

        {headerAction}

        {onClose && (
          <button
            type="button"
            onClick={handleClose}
            aria-label="close"
            className="type-inherit-font flex-col-center shrink-0 w-9 h-9 rounded-full bg-gray2-30 border-none cursor-pointer"
          >
            <span className="material-symbols-outlined text-textcolor-black/50 type-md">close</span>
          </button>
        )}
      </div>

      <div className="w-full h-[2px] bg-gray2-30" />

      {/* 本文 */}
      <div className="flex-col-el w-full p-4 gap-3 bg-white">{children}</div>
    </div>
  );
};
